import type {
  ISnowplowContext,
  ISnowplowUm,
} from '@fanduel/snowplow-um-contract';
import { CoreEvents } from '@fanduel/cpe-um-bundle/core-events/library';
import platform from 'platform-detect';

import { useStore } from '@/store/useStore';
import {
  getSnowplowInstance,
  initializeSnowplow,
} from '@/utilities/logger/snowplow/snowplow-client';
import { getAndroidDistribution } from '@/utilities/logger/amplitude/tracking/helpers';
import {
  isAndroidWrapper,
  isIosWrapper,
} from '@/utilities/helpers/wrapperHelpers';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

/**
 * Snowplow game lifecycle tracking, ported from the legacy launcher
 * (`tracking/snowplow/*`). Events are emitted through `@fanduel/cpe-um-bundle/core-events/library`
 * with `vendorTag: 'SNOWPLOW'` — the same transport the legacy `useSnowplowUm`
 * used under the hood.
 *
 * Events are dispatched through core-events after `initializeSnowplow()` boots
 * SnowplowUm (see `snowplow-client.ts`, ported from the legacy launcher).
 */

export const SNOWPLOW_EVENT_NAMES = {
  GAME_LAUNCH: 'game_launch',
  GAME_EXIT: 'game_exit',
} as const;

export const SNOWPLOW_CONTEXT_MAPPING = {
  GLOBAL_PROPERTIES: 'global_properties',
} as const;

const SNOWPLOW_DEBUG_STORAGE_KEY = 'SNOWPLOW_DEBUG';

const isSnowplowDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(SNOWPLOW_DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const publishSnowplowDebugEvent = (
  eventName: SnowplowEventName,
  builtEvent: SnowplowEvent,
): void => {
  if (!isSnowplowDebugEnabled() || typeof window === 'undefined') {
    return;
  }

  (
    window as Window & { __SNOWPLOW_LAST_EVENT__?: SnowplowEvent }
  ).__SNOWPLOW_LAST_EVENT__ = builtEvent;

  if (
    eventName === SNOWPLOW_EVENT_NAMES.GAME_LAUNCH ||
    eventName === SNOWPLOW_EVENT_NAMES.GAME_EXIT
  ) {
    console.warn(`[SNOWPLOW][DEBUG] ${eventName}`, builtEvent);
  }
};

export type SnowplowEventName =
  (typeof SNOWPLOW_EVENT_NAMES)[keyof typeof SNOWPLOW_EVENT_NAMES];

const GLOBAL_PROPERTIES = SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES;

const getIsMobile = (): boolean =>
  platform.formfactor === 'phone' || platform.formfactor === 'tablet';

const SCHEMAS: Record<string, string> = {
  [GLOBAL_PROPERTIES]:
    'iglu:com.fanduel/casino_global_properties/jsonschema/1-0-0',
  [SNOWPLOW_EVENT_NAMES.GAME_LAUNCH]:
    'iglu:com.fanduel/game_launch/jsonschema/4-0-0',
  [SNOWPLOW_EVENT_NAMES.GAME_EXIT]:
    'iglu:com.fanduel/game_exit/jsonschema/4-0-0',
};

const EVENT_PROP_MAPPINGS: Record<string, Record<string, string>> = {
  [SNOWPLOW_EVENT_NAMES.GAME_LAUNCH]: {
    gamingSessionTrackingId: 'gaming_session_tracking_id',
    gameLaunchSource: 'game_launch_source',
    isShortLinkLaunch: 'is_short_link_launch',
    gameId: 'game_id',
    gameName: 'game_name',
    gameProvider: 'game_provider',
    eventCategory: 'event_category',
  },
  [SNOWPLOW_EVENT_NAMES.GAME_EXIT]: {
    gamingSessionTrackingId: 'gaming_session_tracking_id',
    gameLaunchSource: 'game_launch_source',
    isShortLinkLaunch: 'is_short_link_launch',
    gameId: 'game_id',
    gameName: 'game_name',
    gameProvider: 'game_provider',
    eventCategory: 'event_category',
  },
};

const GLOBAL_PROPERTY_MAPPING: Record<string, string> = {
  product: 'product',
  pageName: 'page_name',
  fullUrl: 'full_url',
  hostname: 'hostname',
  pagePath: 'page_path',
  siteVersion: 'site_version',
  sitePlatform: 'site_platform',
  jurisdiction: 'jurisdiction',
  loginStatus: 'login_status',
};

export type SnowplowEvent = {
  schema: string;
  data: Record<string, unknown>;
  context: { schema: string; data: Record<string, unknown> }[];
};

export const mapPropsToKeys = (
  props: Record<string, unknown>,
  mapping: Record<string, string>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    const mapped = mapping[key];
    if (mapped && value !== undefined && value !== null) {
      result[mapped] = value;
    }
  }
  return result;
};

const toSnowplowProduct = (product: string): string | undefined => {
  switch (product) {
    case 'casino':
      return 'casino_standalone';
    case 'poker':
      return 'poker_standalone';
    case 'xsell':
      return 'casino_xsell';
    default:
      return undefined;
  }
};

const getSiteVersion = ({
  isMobileDevice,
  isWrapper,
}: {
  isMobileDevice: boolean;
  isWrapper: boolean;
}): string => {
  switch (true) {
    case isMobileDevice && !isWrapper:
      return 'mobile_web';
    case isMobileDevice && isWrapper:
      return 'native';
    default:
      return 'desktop';
  }
};

const getSitePlatform = ({
  isMobileDevice,
  isAndroidWrapperDevice,
  isIosWrapperDevice,
}: {
  isMobileDevice: boolean;
  isAndroidWrapperDevice: boolean;
  isIosWrapperDevice: boolean;
}): string => {
  const isWrapper = isAndroidWrapperDevice || isIosWrapperDevice;
  switch (true) {
    case isMobileDevice && !isWrapper:
      return 'mobile_web';
    case isMobileDevice && isWrapper:
      return isIosWrapperDevice ? 'ios' : 'android';
    default:
      return 'desktop';
  }
};

export const getSnowplowProperties = ({
  region,
  userId,
}: {
  region?: string;
  userId?: string | null;
}): Record<string, unknown> => {
  const { product } = useStore.getState().runtime;
  const androidWrapper = isAndroidWrapper();
  const iosWrapper = isIosWrapper();
  const isWrapper = androidWrapper || iosWrapper;
  const isMobileDevice = getIsMobile();

  const props: Record<string, unknown> = {
    fullUrl: window.location.href,
    hostname: window.location.hostname,
    pagePath: window.location.pathname,
    pageName: 'game_window',
    product: product ? toSnowplowProduct(product) : undefined,
    androidDistributionMethod: getAndroidDistribution(),
    siteVersion: getSiteVersion({ isMobileDevice, isWrapper }),
    sitePlatform: getSitePlatform({
      isMobileDevice,
      isAndroidWrapperDevice: androidWrapper,
      isIosWrapperDevice: iosWrapper,
    }),
    loginStatus: userId ? 'logged_in' : 'logged_out',
  };
  if (region) {
    props.jurisdiction = region.toLowerCase();
  }
  return props;
};

export const getSnowplowContext = (
  contextType: typeof GLOBAL_PROPERTIES,
  props: Record<string, unknown>,
): ISnowplowContext => ({
  schema: SCHEMAS[contextType],
  data: mapPropsToKeys(props, GLOBAL_PROPERTY_MAPPING),
});

export const buildSnowplowEvent = (
  eventName: SnowplowEventName,
  eventProps: Record<string, unknown>,
  globalProperties: Record<string, unknown>,
): SnowplowEvent => ({
  schema: SCHEMAS[eventName],
  data: mapPropsToKeys(eventProps, EVENT_PROP_MAPPINGS[eventName]),
  context: [
    {
      schema: SCHEMAS[GLOBAL_PROPERTIES],
      data: mapPropsToKeys(globalProperties, GLOBAL_PROPERTY_MAPPING),
    },
  ],
});

export type SendEventToSnowplowProps = {
  event: SnowplowEventName;
  eventProps: Record<string, unknown>;
  region?: string;
  userId?: string | null;
};

/**
 * Page-view tracking parity with legacy launcher `useSnowplowUm.trackPageView`.
 */
export const trackSnowplowPageView = async (
  eventGlobalProperties: Record<string, unknown>,
): Promise<void> => {
  try {
    const snowplow = await initializeSnowplow();
    snowplow.trackPageView([
      getSnowplowContext(
        SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES,
        eventGlobalProperties,
      ),
    ]);
  } catch (error) {
    Logger.logRumError({
      name: '[SNOWPLOW][UM]track_page_view_failed',
      description: `[SNOWPLOW][UM]track_page_view_failed: ${error}`,
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
    });
  }
};

export { initializeSnowplow as initSnowplow } from './snowplow-client';

/**
 * Build and publish a Snowplow event through core-events. Best-effort: any
 * failure is logged to Datadog and swallowed (analytics must never break the
 * game).
 */
export const sendEventToSnowplow = ({
  event,
  eventProps,
  region,
  userId,
}: SendEventToSnowplowProps): void => {
  try {
    const builtEvent = buildSnowplowEvent(
      event,
      eventProps,
      getSnowplowProperties({ region, userId }),
    );
    publishSnowplowDebugEvent(event, builtEvent);

    const dispatch = (snowplow: ISnowplowUm) => {
      if (userId) {
        snowplow.setUserId(userId);
      }
      CoreEvents?.logEvent({
        name: event,
        data: JSON.stringify(builtEvent),
        vendorTag: 'SNOWPLOW',
      });
    };

    const snowplowInstance = getSnowplowInstance();
    if (snowplowInstance) {
      dispatch(snowplowInstance);
    } else {
      initializeSnowplow()
        .then(dispatch)
        .catch((error) => {
          Logger.logRumError({
            name: '[SNOWPLOW] log_event_failed',
            description: `Failed to emit snowplow event ${event}: ${error}`,
            useCase: DdEventCategory.GameLaunch,
            eventType: DdEventType.Error,
          });
        });
    }
  } catch (error) {
    if (isSnowplowDebugEnabled()) {
      console.error(
        '[SNOWPLOW][DEBUG] sendEventToSnowplow failed',
        event,
        error,
      );
    }
    Logger.logRumError({
      name: '[SNOWPLOW] log_event_failed',
      description: `Failed to emit snowplow event ${event}: ${error}`,
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
    });
  }
};
